import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useConsent } from '../contexts/ConsentContext'
import LogoSvg from '../assets/Logo.svg'
import PurpleArrow from '../assets/purpleArrow.svg'
import 'flag-icons/css/flag-icons.min.css'

// Languages actually loaded in i18n.js (Arabic ar.json exists but isn't wired in).
// `cc` is the ISO-3166 country code used by flag-icons.
const LANGS = [
  { code: 'en', name: 'English', cc: 'gb' },
  { code: 'fr', name: 'Français', cc: 'fr' },
  { code: 'de', name: 'Deutsch', cc: 'de' },
  { code: 'es', name: 'Español', cc: 'es' },
  { code: 'it', name: 'Italiano', cc: 'it' },
  { code: 'ja', name: '日本語', cc: 'jp' },
  { code: 'id', name: 'Bahasa Indonesia', cc: 'id' },
  { code: 'ko', name: '한국어', cc: 'kr' },
  { code: 'nl', name: 'Nederlands', cc: 'nl' },
  { code: 'pl', name: 'Polski', cc: 'pl' },
  { code: 'pt', name: 'Português', cc: 'pt' },
  { code: 'ru', name: 'Русский', cc: 'ru' },
  { code: 'tr', name: 'Türkçe', cc: 'tr' },
  { code: 'zh', name: '中文', cc: 'cn' },
]

// ── Styles ──────────────────────────────────────────────────────────────────
// Every block (category header, each cookie, about-tab text) uses the same
// uniform style — no nested/wrapping background, matching the design.
const blockStyle = {
  background: 'rgba(255, 255, 255, 0.06)',
  padding: '16px 12px',
  borderRadius: '14px',
  marginBottom: '10px',
}

const cookieToggleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  padding: '2px 4px',
  cursor: 'pointer',
  color: '#8B4AF2',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '140%',
  letterSpacing: '-0.01em',
  textAlign: 'center',
  textTransform: 'uppercase',
}

const tabBarStyle = {
  display: 'flex',
  gap: '6px',
  padding: '4px',
  marginBottom: '16px',
  borderRadius: '14px',
  background: 'var(--Opacity_White-6, #FFFFFF0F)',
  border: '1px solid var(--Opacity_White-12, #FFFFFF1F)',
  boxShadow: '0px 0px 20.78px 0px #00000017, 0px 0px 127.88px 0px #00000073',
}

const tabTextStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '140%',
  letterSpacing: '-0.01em',
  color: '#FFFFFF',
}

const tabBtnBase = {
  ...tabTextStyle,
  position: 'relative',
  flex: 1,
  height: '40px',
  borderRadius: '10px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const tabBtnActiveStyle = {
  ...tabBtnBase,
  background: 'linear-gradient(98.81deg, rgba(177, 76, 238, 0.3) 6.57%, rgba(128, 73, 243, 0.3) 100%)',
  boxShadow: '0px 0px 20.78px 0px #00000017, 0px 0px 127.88px 0px #0000002E',
}

const labelCell = {
  flex: '0 0 38%',
  paddingRight: '8px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '140%',
  letterSpacing: '0',
  color: '#FFFFFF80',
}
const valueCell = {
  flex: '1 1 auto',
  wordBreak: 'break-word',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '140%',
  letterSpacing: '0',
  color: '#FFFFFF',
}
const cookieRow = { display: 'flex', alignItems: 'flex-start', padding: '6px 0' }

// Round flag (flag-icons SVG cropped to a circle).
function Flag({ cc }) {
  return (
    <span
      className={`fi fi-${cc}`}
      style={{
        display: 'block',
        width: '22px',
        height: '22px',
        flexShrink: 0,
        borderRadius: '50%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
  )
}

function Toggle({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '38px',
        height: '22px',
        borderRadius: '999px',
        padding: 0,
        flexShrink: 0,
        position: 'relative',
        boxSizing: 'border-box',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        background: 'var(--Opacity_White-12, #FFFFFF1F)',
        border: '1px solid var(--Opacity_White-12, #FFFFFF1F)',
        boxShadow: '0px 0px 20.78px 0px #00000017, 0px 0px 127.88px 0px #0000002E',
        transition: 'opacity 0.2s ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '18px' : '2px',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          boxSizing: 'border-box',
          background: 'linear-gradient(98.81deg, rgba(177, 76, 238, 0.8) 6.57%, rgba(128, 73, 243, 0.8) 100%)',
          border: '1px solid var(--Opacity_White-12, #FFFFFF1F)',
          boxShadow: '0px 1px 6.66px 0px #00000017, 0px 5px 41px 0px #0000002E',
          opacity: checked ? 1 : 0.5,
          transition: 'left 0.2s ease, opacity 0.2s ease',
        }}
      />
    </button>
  )
}

function CookieCard({ cookie }) {
  const { t } = useTranslation()
  return (
    <div style={blockStyle}>
      <div style={cookieRow}>
        <span style={labelCell}>{t('cookieManager.labelName')}</span>
        <span style={valueCell}>{cookie.name}</span>
      </div>
      <div style={cookieRow}>
        <span style={labelCell}>{t('cookieManager.labelProvider')}</span>
        <span style={{ ...valueCell, color: '#8B4AF2' }}>{cookie.provider}</span>
      </div>
      <div style={cookieRow}>
        <span style={labelCell}>{t('cookieManager.labelExpiration')}</span>
        <span style={valueCell}>{cookie.expiration}</span>
      </div>
      <div style={cookieRow}>
        <span style={labelCell}>{t('cookieManager.labelExpiration')}</span>
        <span style={valueCell}>{cookie.purpose}</span>
      </div>
    </div>
  )
}

function CategoryBlock({ cat, checked, onToggle }) {
  const { t } = useTranslation()
  // Cookie list is expanded by default (design shows "HIDE COOKIES").
  const [open, setOpen] = useState(true)
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={blockStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', lineHeight: '120%', letterSpacing: '-0.02em', color: '#FFFFFF' }}>{cat.name}</span>
          <Toggle checked={checked} disabled={cat.locked} onChange={onToggle} />
        </div>
        <p style={{ margin: '8px 0 0', fontFamily: 'Poppins, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '140%', letterSpacing: '-0.01em', color: '#FFFFFF80' }}>{cat.description}</p>
      </div>

      {open && cat.cookies.map(c => <CookieCard key={c.name} cookie={c} />)}

      {cat.cookies.length > 0 && (
        <button type="button" onClick={() => setOpen(o => !o)} style={cookieToggleStyle}>
          {open ? t('cookieManager.hideCookies') : t('cookieManager.showCookies')}
          <img
            src={PurpleArrow}
            alt=""
            width={16}
            height={16}
            style={{
              width: '16px',
              height: '16px',
              transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.25s ease',
            }}
          />
        </button>
      )}
    </div>
  )
}

export default function CookieManager() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { consent, cookiesAccepted, applyConsent } = useConsent()

  // Inherit the background of the route we came from (falls back to the common
  // dark theme so the default maroon body background never shows).
  useEffect(() => {
    const fromClass = location.state?.fromBodyClass
    const classes = (fromClass && fromClass.trim())
      ? fromClass.trim().split(/\s+/)
      : ['ndg-mode', 'quiz-mode']
    classes.forEach(c => document.body.classList.add(c))
    return () => classes.forEach(c => document.body.classList.remove(c))
  }, [location.state])

  const [tab, setTab] = useState('declaration')
  const [langOpen, setLangOpen] = useState(false)
  // First visit (undecided) → pre-checked optional categories, matching the design.
  const firstVisit = cookiesAccepted === undefined
  const [prefs, setPrefs] = useState({
    performance: firstVisit ? true : consent.performance,
    targeting: firstVisit ? true : consent.targeting,
    unclassified: firstVisit ? true : consent.unclassified,
  })

  // Built from translations so it re-renders on language change.
  const CATEGORIES = [
    {
      key: 'necessary',
      name: t('cookieManager.catNecessaryName'),
      locked: true,
      description: t('cookieManager.catNecessaryDesc'),
      cookies: [
        // Скрыто по запросу — оставлено в коде, не удалено.
        // { name: 'X-AB', provider: 'Stack Exchange Inc/static.net', expiration: t('cookieManager.cookies.xab.expiration'), purpose: t('cookieManager.cookies.xab.purpose') },
        // { name: 'CookieScriptConsent', provider: 'CookieScript / swipey.ai', expiration: t('cookieManager.cookies.consent.expiration'), purpose: t('cookieManager.cookies.consent.purpose') },
        // { name: '_GRECAPTCHA', provider: 'Google LLC / www.recaptcha.net', expiration: t('cookieManager.cookies.recaptcha.expiration'), purpose: t('cookieManager.cookies.recaptcha.purpose') },
        // { name: '_chat_chat_session', provider: 'swipey.ai', expiration: t('cookieManager.cookies.chat.expiration'), purpose: t('cookieManager.cookies.chat.purpose') },
      ],
    },
    { key: 'performance', name: t('cookieManager.catPerformanceName'), description: t('cookieManager.catPerformanceDesc'), cookies: [] },
    { key: 'targeting', name: t('cookieManager.catTargetingName'), description: t('cookieManager.catTargetingDesc'), cookies: [] },
    { key: 'unclassified', name: t('cookieManager.catUnclassifiedName'), description: t('cookieManager.catUnclassifiedDesc'), cookies: [] },
  ]

  const close = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }


  const acceptAll = () => {
    applyConsent({ performance: true, targeting: true, unclassified: true })
    close()
  }
  // const saveAndClose = () => {
  //   applyConsent({ performance: true, targeting: true, unclassified: true })
  //   close()
  // }

  const current = LANGS.find(l => l.code === i18n.language) || LANGS[0]
  const selectLang = code => {
    i18n.changeLanguage(code)
    setLangOpen(false)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        padding: '28px 16px 48px',
        fontFamily: 'Poppins, sans-serif',
        color: '#fff',
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <img src={LogoSvg} alt="Swipey" width={120} height={29} />
        </div>

        {/* Language selector */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', position: 'relative' }}>
          <button
            type="button"
            onClick={() => setLangOpen(o => !o)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '12px',
              padding: '6px 12px',
              cursor: 'pointer',
              color: '#fff',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            <Flag cc={current.cc} />
            <span>{current.name}</span>
            <span style={{ fontSize: '9px', opacity: 0.8, transform: langOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>▼</span>
          </button>

          {langOpen && (
            <>
              <div onClick={() => setLangOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  zIndex: 20,
                  width: '210px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '6px',
                  borderRadius: '12px',
                  background: 'rgba(22, 15, 30, 0.98)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.55)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                {LANGS.map(l => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => selectLang(l.code)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      textAlign: 'left',
                      background: l.code === current.code ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 10px',
                      cursor: 'pointer',
                      color: '#fff',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    <Flag cc={l.cc} />
                    <span>{l.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Tabs — both inside one pill */}
        <div style={tabBarStyle}>
          {[
            { key: 'declaration', label: t('cookieManager.tabDeclaration') },
            { key: 'about', label: t('cookieManager.tabAbout') },
          ].map(tb => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className={tab === tb.key ? 'cookie-tab-active' : undefined}
              style={tab === tb.key ? tabBtnActiveStyle : tabBtnBase}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'declaration' ? (
          <div>
            {CATEGORIES.map(cat => (
              <CategoryBlock
                key={cat.key}
                cat={cat}
                checked={cat.locked ? true : prefs[cat.key]}
                onToggle={val => setPrefs(p => ({ ...p, [cat.key]: val }))}
              />
            ))}
          </div>
        ) : (
          <div style={blockStyle}>
            <p style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '140%', letterSpacing: '-0.01em', color: '#FFFFFF80' }}>
              {t('cookieManager.aboutP1')}
            </p>
            <p style={{ margin: '14px 0 0', fontFamily: 'Poppins, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '140%', letterSpacing: '-0.01em', color: '#FFFFFF80' }}>
              {t('cookieManager.aboutP2')}
            </p>
          </div>
        )}

        {/* Actions — only on the Cookie declaration tab */}
        {tab === 'declaration' && (
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={acceptAll}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '12px', fontWeight: 500, padding: '2px' }}
            >
              {t('cookieManager.onlyNecessary')}
            </button>

            <button
              type="button"
              onClick={acceptAll}
              style={{
                width: '100%',
                height: '44px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 500,
                background: 'linear-gradient(98.81deg, #B14CEE 6.57%, #8049F3 100%)',
                boxShadow: '0px 1px 4.22px 0px #681AC74E, 0px 5px 26px 0px #681AC79C',
              }}
            >
              {t('cookieManager.acceptAll')}
            </button>

            <button
              type="button"
              onClick={acceptAll}
              style={{
                width: '100%',
                height: '44px',
                borderRadius: '12px',
                cursor: 'pointer',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 500,
                border: '1px solid rgba(255, 255, 255, 0.12)',
                background: 'linear-gradient(98.81deg, rgba(177,76,238,0.35) 6.57%, rgba(128,73,243,0.35) 100%)',
              }}
            >
              {t('cookieManager.saveClose')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
